import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { DraggableComponentProps } from 'features/document/constants';
import { isPricingTableElement } from 'utils/typeGuards';
import '../Table/Table.css';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { updateContentElementById, updateElementById } from 'features/document/documentSlice';
import { selectQuote } from 'features/salesforce/salesforceSlice';
import { parseQuoteLineItemsToPricingTable } from './PricingTable.utils';
import { docGeniusPalette } from 'styles/partials/colors';

export default function PricingTable({ currentElement }: DraggableComponentProps) {
  if (!isPricingTableElement(currentElement)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const quote = useAppSelector(selectQuote);

  const tableContent = currentElement.content;
  const {
    activeProduct,
    columns,
    rows,
    cells,
    resume,
    products,
    hideDiscountColumn,
    hideListPriceColumn,
    hideSummary,
    hideDescriptionColumn,
    showDescriptionAsImage,
    headerColor,
  } = tableContent;

  const activeProductIndex = products?.findIndex(product => product.Id === activeProduct?.Id);
  const headerTitles = columns.map(column => column.header);

  const handleCellClick = (rowIndex: number) => {
    if (products) {
      dispatch(
        updateContentElementById({
          content: {
            ...tableContent,
            activeProduct: products[rowIndex],
          },
          style: {},
        })
      );
    }
  };

  const renderCell = (cellContent: string, columnId: string, rowIndex: number) => {
    let formattedContent = cellContent;

    // 6 -> Description cell (Special case)
    if (columnId === 'column-7') {
      if (formattedContent && formattedContent.length > 0) {
        return showDescriptionAsImage ? (
          <img src={cellContent} alt={`product-img-${rowIndex + 1}`} width={'100%'} style={{ objectFit: 'cover' }} />
        ) : (
          <span style={{ wordWrap: 'break-word', maxWidth: '72px' }}>{formattedContent}</span>
        );
      }

      return <span style={{ wordWrap: 'break-word', maxWidth: '72px' }}>{formattedContent}</span>;
    }

    // 0, 3 & 4 -> Numbers cells
    if (columnId !== 'column-1' && columnId !== 'column-4' && columnId !== 'column-5') {
      formattedContent = Number(cellContent).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return <span style={{ wordWrap: 'break-word', maxWidth: '72px' }}>{formattedContent}</span>;
  };

  const renderTableHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map(column => (
            <th
              className='pricingHeader'
              key={column.id}
              style={{
                display: column.hidden ? 'none' : 'table-cell',
                backgroundColor: headerColor || docGeniusPalette.tableHeader,
              }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr className={`row ${activeProductIndex === rowIndex ? 'activeRow' : 'pricingRow'}`} key={row.id}>
            {row.cellIds.map((cellId, cellIndex) => {
              const cell = cells[cellId];
              return (
                <td
                  className='pricingCell'
                  colSpan={cell.colSpan}
                  key={cell.id}
                  style={{ display: cell.hidden ? 'none' : 'table-cell' }}
                  onClick={() => {
                    handleCellClick(rowIndex);
                  }}
                  aria-hidden="true"
                >
                  {renderCell(cell.content, columns[cellIndex].id, rowIndex)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  const formatResumeCell = (key: string, value: string) => {
    const formattedKey = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

    return {
      formattedKey,
      formattedValue: Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  };

  const renderPricingResume = () => {
    if (!resume) {
      return null;
    }

    const rows = [];
    for (const [key, value] of Object.entries(resume)) {
      if (hideDiscountColumn && key === 'discount') {
        continue;
      }

      const { formattedKey, formattedValue } = formatResumeCell(key, value);

      rows.push(
        <tr className='row' key={key}>
          <td className='resumeCell labelResumeCell'>{formattedKey}:</td>
          <td className='resumeCell valueResumeCell'>{formattedValue}</td>
        </tr>
      );
    }

    return <tbody>{rows}</tbody>;
  };

  useEffect(() => {
    if (quote.Id !== '') {
      const content = parseQuoteLineItemsToPricingTable(
        quote.QuoteLineItems,
        quote.Tax,
        quote.ShippingHandling,
        headerColor,
        headerTitles,
        hideDiscountColumn,
        hideListPriceColumn,
        hideSummary,
        hideDescriptionColumn,
        showDescriptionAsImage
      );

      dispatch(
        updateElementById({
          elementId: currentElement.id,
          content,
          style: {},
        })
      );
    }
  }, [dispatch, quote]);

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'} gap={'24px'}>
      <table className='table'>
        {renderTableHeader()}
        {renderTableBody()}
      </table>
      {!hideSummary && <table className='resumeTable'>{renderPricingResume()}</table>}
    </Box>
  );
}
